import { ChevronDown, ChevronRight, ChevronUp, Info, UserPlus, Users } from 'lucide-react';
import React, { useState } from 'react';
import { productCompactDisplayLabel } from '../../../../../shared/utils/productDisplayLabel';
import { useTeamTab } from '../../TeamTabContext';

export const SuggestPeoplePanel: React.FC = () => {
  const {
    canViewTeam,
    canModifyTeam,
    teamSuggestionsLoading,
    teamSuggestionsError,
    teamSuggestionsData,
    teamSuggestionSelected,
    setTeamSuggestionSelected,
    teamSuggestionProductOverrides,
    setTeamSuggestionProductOverrides,
    expandedSuggestions,
    setExpandedSuggestions,
    handleFetchTeamSuggestions,
    handleBulkAddTeamSuggestions,
  } = useTeamTab();

  // Default-collapsed per redesign plan; one click reveals the full panel
  const [open, setOpen] = useState(false);

  if (!canViewTeam) return null;

  const candidatesCount = teamSuggestionsData?.candidates.length ?? 0;
  const summary = teamSuggestionsData
    ? `${candidatesCount} new candidate${candidatesCount !== 1 ? 's' : ''} (last fetch)`
    : '12-month activity scan; click to fetch suggestions';

  return (
    <div className="settings-collapsible">
      <button
        type="button"
        className={`settings-collapsible__trigger${open ? ' settings-collapsible__trigger--open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown size={16} className="settings-collapsible__trigger-icon" />
        ) : (
          <ChevronRight size={16} className="settings-collapsible__trigger-icon" />
        )}
        <UserPlus size={16} aria-hidden />
        <span style={{ flex: 1, textAlign: 'left' }}>
          Suggest people from organization activity
        </span>
        <span style={{ color: 'var(--color-text-muted)', fontSize: '12px', fontWeight: 400 }}>
          {summary}
        </span>
      </button>
      {open && (
        <div className="settings-collapsible__body">
          <div
            style={{
              padding: '20px',
              backgroundColor: 'var(--profile-nested-bg-info)',
              borderRadius: '12px',
              border: '1px solid var(--profile-nested-border-info)',
            }}
          >
            <h3
              style={{
                marginBottom: '8px',
                fontSize: '15px',
                fontWeight: '600',
                color: 'var(--color-text)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <UserPlus size={18} aria-hidden />
              Suggest people from organization activity
              <span
                className="stat-hint-icon"
                title="Touches = sum of worklog authorships, issue assignee changes, and issue reporter records inside the window. Total equals the sum of per-product values."
                style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}
              >
                <Info size={14} style={{ color: 'var(--color-text-muted)' }} />
              </span>
            </h3>
            <p
              style={{
                marginBottom: '12px',
                fontSize: '12px',
                color: 'var(--color-text-secondary)',
                lineHeight: 1.45,
              }}
            >
              Finds names from worklog authors and issue assignee/reporter across all products in
              the organization (last 12 months, min. 1 touch). Matches by display name — review
              before adding. Product checkboxes are pre-selected where the person has activity.
            </p>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                marginBottom: '12px',
              }}
            >
              <button
                type="button"
                onClick={handleFetchTeamSuggestions}
                disabled={teamSuggestionsLoading}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  backgroundColor: teamSuggestionsLoading ? '#9ca3af' : '#0284c7',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: teamSuggestionsLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {teamSuggestionsLoading ? (
                  <>Loading\u2026</>
                ) : (
                  <>
                    <Users size={16} aria-hidden />
                    Load suggestions
                  </>
                )}
              </button>
              {canModifyTeam &&
                teamSuggestionsData &&
                teamSuggestionsData.candidates.length > 0 && (
                  <button
                    type="button"
                    onClick={handleBulkAddTeamSuggestions}
                    disabled={teamSuggestionsLoading}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      backgroundColor: teamSuggestionsLoading ? '#9ca3af' : '#6366f1',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: teamSuggestionsLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Add selected to roster
                  </button>
                )}
            </div>
            {teamSuggestionsError && (
              <p style={{ color: '#b91c1c', fontSize: '13px', marginBottom: '8px' }}>
                {teamSuggestionsError}
              </p>
            )}
            {teamSuggestionsData && (
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '10px',
                }}
              >
                Window: since {teamSuggestionsData.filters.sinceCutoff} (
                {teamSuggestionsData.filters.sinceMonths} mo., min.{' '}
                {teamSuggestionsData.filters.minTouches} touch
                {teamSuggestionsData.filters.minTouches !== 1 ? 'es' : ''})
              </div>
            )}
            {teamSuggestionsData && teamSuggestionsData.candidates.length === 0 && (
              <p style={{ fontSize: '13px', color: '#64748b' }}>
                No new candidates — everyone with activity may already be on the team, or sync more
                worklogs/issues first.
              </p>
            )}
            {teamSuggestionsData &&
              teamSuggestionsData.candidates.length > 0 &&
              (() => {
                const products = teamSuggestionsData.products || [];
                const productLabel = (pid: string) => {
                  const p = products.find((pr) => pr.id === pid);
                  return p ? productCompactDisplayLabel(p) : pid.slice(0, 8);
                };
                return (
                  <div
                    style={{
                      border: '1px solid var(--profile-nested-border-info)',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      backgroundColor: 'var(--profile-nested-table-row-bg)',
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '40px 1fr 80px 100px 28px',
                        gap: '8px',
                        padding: '8px 12px',
                        backgroundColor: 'var(--profile-nested-table-header-bg)',
                        fontWeight: 600,
                        color: 'var(--color-text-secondary)',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                      }}
                    >
                      <span />
                      <span>Name</span>
                      <span style={{ textAlign: 'right' }}>Touches</span>
                      <span>Last seen</span>
                      <span />
                    </div>
                    {teamSuggestionsData.candidates.map((c) => {
                      const isExpanded = expandedSuggestions.has(c.displayName);
                      const activeProducts = Object.entries(c.touchesByProduct || {})
                        .filter(([, t]) => t > 0)
                        .sort(([a], [b]) => a.localeCompare(b));
                      const overrides = teamSuggestionProductOverrides[c.displayName];
                      const selectedProductIds = overrides
                        ? new Set(overrides)
                        : new Set(activeProducts.map(([pid]) => pid));

                      return (
                        <div key={c.displayName}>
                          <label
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '40px 1fr 80px 100px 28px',
                              gap: '8px',
                              padding: '10px 12px',
                              alignItems: 'center',
                              borderTop: '1px solid var(--profile-nested-border)',
                              cursor: canModifyTeam ? 'pointer' : 'default',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={teamSuggestionSelected.has(c.displayName)}
                              onChange={() => {
                                setTeamSuggestionSelected((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(c.displayName)) next.delete(c.displayName);
                                  else next.add(c.displayName);
                                  return next;
                                });
                              }}
                              disabled={!canModifyTeam || teamSuggestionsLoading}
                              aria-label={`Select ${c.displayName}`}
                            />
                            <span style={{ fontWeight: 500, color: 'var(--color-text)' }}>
                              {c.displayName}
                            </span>
                            <span
                              style={{
                                textAlign: 'right',
                                color: 'var(--color-text-secondary)',
                              }}
                            >
                              {c.totalTouches}
                            </span>
                            <span
                              style={{
                                color: 'var(--color-text-muted)',
                                fontSize: '11px',
                              }}
                            >
                              {c.lastSeen || '\u2014'}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setExpandedSuggestions((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(c.displayName)) next.delete(c.displayName);
                                  else next.add(c.displayName);
                                  return next;
                                });
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                              }}
                              aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            >
                              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                          </label>
                          {isExpanded && (
                            <div
                              style={{
                                paddingLeft: '60px',
                                paddingRight: '12px',
                                paddingBottom: '10px',
                                fontSize: '12px',
                                color: 'var(--color-text-secondary)',
                                borderTop: '1px solid var(--profile-nested-border)',
                              }}
                            >
                              {activeProducts.length === 0 && (
                                <div style={{ padding: '4px 0', fontStyle: 'italic' }}>
                                  No per-product breakdown
                                </div>
                              )}
                              {activeProducts.map(([pid, touches]) => (
                                <label
                                  key={pid}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    marginRight: '14px',
                                    marginTop: '4px',
                                    cursor: canModifyTeam ? 'pointer' : 'default',
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedProductIds.has(pid)}
                                    disabled={!canModifyTeam || teamSuggestionsLoading}
                                    onChange={() => {
                                      setTeamSuggestionProductOverrides((prev) => {
                                        const current = prev[c.displayName]
                                          ? [...prev[c.displayName]]
                                          : activeProducts.map(([p]) => p);
                                        const idx = current.indexOf(pid);
                                        if (idx >= 0) current.splice(idx, 1);
                                        else current.push(pid);
                                        return {
                                          ...prev,
                                          [c.displayName]: current,
                                        };
                                      });
                                    }}
                                  />
                                  <span>
                                    {productLabel(pid)}: {touches}
                                  </span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            {teamSuggestionsData && teamSuggestionsData.existingMatches.length > 0 && (
              <details
                style={{
                  marginTop: '12px',
                  fontSize: '12px',
                  color: 'var(--color-text-secondary)',
                }}
              >
                <summary style={{ cursor: 'pointer', fontWeight: 500 }}>
                  Already on roster ({teamSuggestionsData.existingMatches.length})
                </summary>
                <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
                  {teamSuggestionsData.existingMatches.map((m) => (
                    <li key={m.existingPersonId}>
                      {m.displayName} — {m.totalTouches} touch
                      {m.totalTouches !== 1 ? 'es' : ''}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
